import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CancelOutlined as CancelIcon,
  Download as DownloadIcon,
  PlayArrow as StartIcon,
  Refresh as RetryIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';

import conversationService, {
  BlobDownload,
  ConversationCloseImportJob,
  ConversationCloseImportRow,
} from '../../services/conversationService';

interface BulkCloseDialogProps {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

const ACTIVE_STATUSES = new Set(['queued', 'running']);
const TERMINAL_STATUSES = new Set(['completed', 'completed_with_errors', 'cancelled']);

const statusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    validated: 'Validado',
    queued: 'En cola',
    running: 'Procesando',
    completed: 'Completado',
    completed_with_errors: 'Completado con novedades',
    cancelled: 'Cancelado',
    processing: 'Procesando',
    succeeded: 'Completado',
    partial: 'Parcial',
    skipped: 'Omitido',
    failed: 'Fallido',
  };
  return labels[status] || status;
};

const rowColor = (
  status: string
): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  if (status === 'succeeded') return 'success';
  if (status === 'partial' || status === 'skipped') return 'warning';
  if (status === 'failed') return 'error';
  if (status === 'processing') return 'info';
  return 'default';
};

const errorMessage = (error: unknown): string => {
  const detail = (error as any)?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (error instanceof Error && error.message) return error.message;
  return 'No fue posible completar la operación.';
};

const downloadBlob = ({ blob, filename }: BlobDownload): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename || 'archivo.xlsx';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const rowDetail = (row: ConversationCloseImportRow): string => {
  return row.error_message || row.warning_message || row.stage_decision || '—';
};

const BulkCloseDialog: React.FC<BulkCloseDialogProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [job, setJob] = useState<ConversationCloseImportJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const terminal = Boolean(job && TERMINAL_STATUSES.has(job.status));
  const active = Boolean(job && ACTIVE_STATUSES.has(job.status));
  const previewRows = useMemo(() => (job?.rows || []).slice(0, 100), [job?.rows]);
  const jobId = job?.job_id;
  const jobStatus = job?.status;

  useEffect(() => {
    if (!open || !jobId || !jobStatus || !ACTIVE_STATUSES.has(jobStatus)) {
      return undefined;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const nextJob = await conversationService.getConversationCloseImport(jobId);
        if (cancelled) return;
        if (TERMINAL_STATUSES.has(nextJob.status)) {
          const rows = await conversationService.getConversationCloseImportRows(
            nextJob.job_id,
            100
          );
          if (cancelled) return;
          nextJob.rows = rows.rows;
          onCompleted?.();
        }
        setJob((previous) => ({
          ...nextJob,
          rows: nextJob.rows.length ? nextJob.rows : previous?.rows || [],
        }));
      } catch (pollError) {
        if (!cancelled) setError(errorMessage(pollError));
      }
    };

    const intervalId = window.setInterval(poll, 2500);
    void poll();
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [jobId, jobStatus, onCompleted, open]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
    setJob(null);
    setError(null);
  };

  const handleTemplateDownload = async () => {
    setBusy(true);
    setError(null);
    try {
      downloadBlob(await conversationService.downloadConversationCloseTemplate());
    } catch (downloadError) {
      setError(errorMessage(downloadError));
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      setJob(await conversationService.validateConversationCloseImport(file));
    } catch (validationError) {
      setError(errorMessage(validationError));
    } finally {
      setBusy(false);
    }
  };

  const handleQueue = async () => {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const queued = await conversationService.queueConversationCloseImport(job.job_id);
      queued.rows = job.rows;
      setJob(queued);
    } catch (queueError) {
      setError(errorMessage(queueError));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const cancelled = await conversationService.cancelConversationCloseImport(job.job_id);
      const rows = await conversationService.getConversationCloseImportRows(job.job_id, 100);
      cancelled.rows = rows.rows;
      setJob(cancelled);
    } catch (cancelError) {
      setError(errorMessage(cancelError));
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async () => {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const retried = await conversationService.retryConversationCloseImport(job.job_id);
      retried.rows = job.rows;
      setJob(retried);
    } catch (retryError) {
      setError(errorMessage(retryError));
    } finally {
      setBusy(false);
    }
  };

  const handleResultsDownload = async () => {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      downloadBlob(await conversationService.downloadConversationCloseResults(job.job_id));
    } catch (downloadError) {
      setError(errorMessage(downloadError));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    onClose();
    if (!active) {
      setFile(null);
      setJob(null);
      setError(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Cerrar conversaciones desde Excel</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Alert severity="info">
            Se cierra únicamente la conversación activa vinculada al deal importado y se
            cancelan sus seguimientos. El cierre no convierte automáticamente el negocio
            en perdido. En HubSpot solo se cambia Calificado a Producto presentado o un
            estado local Perdido a Perdido; las etapas terminales existentes quedan
            protegidas.
          </Alert>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              1. Prepara el archivo
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Usa la plantilla de una sola columna, <code>hubspot_deal_id</code>. Se
              admiten hasta 10.000 filas.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleTemplateDownload}
              disabled={busy}
            >
              Descargar plantilla
            </Button>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              2. Valida los deals
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={active || busy}
              >
                {file ? 'Cambiar archivo' : 'Seleccionar .xlsx'}
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                />
              </Button>
              <Button
                variant="contained"
                onClick={handleValidate}
                disabled={!file || active || busy}
              >
                Validar archivo
              </Button>
            </Stack>
            {file && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Archivo: {file.name}
              </Typography>
            )}
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          {job && (
            <>
              <Divider />
              <Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Trabajo {job.job_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {job.source_filename}
                    </Typography>
                  </Box>
                  <Chip
                    label={statusLabel(job.status)}
                    color={
                      job.status === 'completed'
                        ? 'success'
                        : job.status === 'completed_with_errors'
                        ? 'warning'
                        : job.status === 'cancelled'
                        ? 'default'
                        : 'info'
                    }
                  />
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, job.progress_percent))}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {job.processed_rows} de {job.total_rows} filas procesadas (
                  {job.progress_percent}%)
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5, gap: 1 }}>
                  <Chip label={`Válidas: ${job.valid_rows}`} color="info" variant="outlined" />
                  <Chip label={`Exitosas: ${job.succeeded_rows}`} color="success" variant="outlined" />
                  <Chip label={`Parciales: ${job.partial_rows}`} color="warning" variant="outlined" />
                  <Chip label={`Omitidas: ${job.skipped_rows}`} variant="outlined" />
                  <Chip label={`Fallidas: ${job.failed_rows}`} color="error" variant="outlined" />
                </Stack>
              </Box>

              {previewRows.length > 0 && (
                <TableContainer sx={{ maxHeight: 320, border: '1px solid', borderColor: 'divider' }}>
                  <Table size="small" stickyHeader aria-label="Vista previa del cierre masivo">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fila</TableCell>
                        <TableCell>Deal ID</TableCell>
                        <TableCell>Estado local</TableCell>
                        <TableCell>Resultado</TableCell>
                        <TableCell>Detalle</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.row_number}</TableCell>
                          <TableCell>{row.hubspot_deal_id || '—'}</TableCell>
                          <TableCell>{row.lead_status || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={statusLabel(row.status)}
                              color={rowColor(row.status)}
                            />
                          </TableCell>
                          <TableCell>{rowDetail(row)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {active && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">
                    El proceso continúa aunque cierres esta ventana.
                  </Typography>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap' }}>
        {job?.status === 'validated' && (
          <Button
            variant="contained"
            startIcon={<StartIcon />}
            onClick={handleQueue}
            disabled={busy || job.valid_rows === 0}
          >
            Iniciar cierre
          </Button>
        )}
        {active && (
          <Button
            color="warning"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={busy}
          >
            Cancelar pendientes
          </Button>
        )}
        {terminal && (job?.partial_rows || 0) + (job?.failed_rows || 0) > 0 && (
          <Button startIcon={<RetryIcon />} onClick={handleRetry} disabled={busy}>
            Reintentar pendientes CRM
          </Button>
        )}
        {terminal && job && (
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleResultsDownload}
            disabled={busy}
          >
            Descargar resultados
          </Button>
        )}
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkCloseDialog;
